import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Switch, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik, FormikProps } from "formik"
import { useLocation } from "react-router-dom"
import { HttpStatusCode } from "axios";
import Select from "react-select";

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import Loader from "@/components/Layouts/Loader";
import { COLORS, DURATION_TOAST, USER_STATUS } from "@/common/constants";
import SelectRole from "@/components/Selects/SelectRole";
import SelectUserTypeRefData from "@/components/Selects/SelectUserTypeRefData";
import SelectInspector from "@/components/Selects/SelectInspector";
import SelectShipOwner from "@/components/Selects/SelectShipOwner";
import { addUserSchema } from "@/schemas/addUserSchema";
import { getDataInspector } from "@/services/inspectorService";
import { getShipOwnerData } from "@/services/shipOwnerService";
import { postInspectionUsers, putInspectionUsers } from "@/services/inspectionUserService";
import { getRoleData } from "@/services/roleService";
import { getDataRef, RefData } from "@/services/refDataService";
import { Option, option } from "@/utils/select";
import { getListSireReviewRequestingCompany } from "@/services/sireReviewService";
import { getCustomStyles } from "@/utils/customStyleSelect";

type FormContents = {
    role: string,
    type: string,
    inspectingCompany: string,
    shipOperator: string,
    requestingCompany: string,
    email: string,
    firstName: string,
    lastName: string,
    status: number,
};

// these roles are not the same as the ones from constants.ts
enum Roles {
    DEFAULT = '',
    SUPER_ADMIN = 'superadmin',
    FINANCE = 'adminfinance',
    INSPECTING_COMPANY = 'inspectingcompany',
    SHIP_OPERATOR = 'shipoperator',
    REQUESTOR = 'requestor',
};

const EditUser = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const { id, role, inspectingCompany, shipOperator, requestingCompany, email, firstName, lastName, userType, status } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [listInspector, setListInspector] = useState<Option[]>([]);
    const [listShipOwner, setListShipOwner] = useState<Option[]>([]);
    const [listRequestingCompany, setListRequestingCompany] = useState<Option[]>([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [listRole, setListRole] = useState<any[]>([]);
    const [listUserType, setListUserType] = useState<any[]>([]);

    const handleCancel = () => {
        onOpen();
        setTitle(`Cancel ${id ? "Changes" : "Registraton"}?`);
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = (data: any) => {
        setTitle(`${id ? "Update" : "Register"} This User?`);
        setConfirmationTest(`Are you sure you want to ${(id ? "update " : "register ") + data.email}?`);
        setConfirmButtonText(id ? "Update User" : "Register User");
        setCancelButtonText("Cancel");
        onOpen();
        setIsSubmitting(true);
        setDataEdit(data);
    }

    const getCompanyName = (role: Roles) => {
        let company: Option | undefined = undefined;
        switch (role){
            case Roles.INSPECTING_COMPANY:
                company = listInspector.find((inspector) => inspector.value === dataEdit?.inspectingCompany);
                break;
            case Roles.SHIP_OPERATOR:
                company = listShipOwner.find((shipOwner) => shipOwner.value === dataEdit?.shipOperator);
                break;
            case Roles.REQUESTOR:
                company = listRequestingCompany.find((company) => (company.value === dataEdit?.requestingCompany));
                break;
            default:
                // do nothing
        }
        return (company?.label ?? '')
    }

    const generatedataAdd = () => {
        const data = {
            email: dataEdit?.email,
            password: "",
            firstName: dataEdit?.firstName,
            lastName: dataEdit?.lastName,
            role: dataEdit?.role,
            userType: dataEdit?.type
        };

        switch (data.role){
            case Roles.INSPECTING_COMPANY:
                return { ...data, appId: dataEdit?.inspectingCompany, companyName: getCompanyName(data.role) };
            case Roles.SHIP_OPERATOR:
                return { ...data, appId: dataEdit?.shipOperator, companyName: getCompanyName(data.role) };
            case Roles.REQUESTOR:
                return { ...data, appId: dataEdit?.requestingCompany, companyName: getCompanyName(data.role) };
            default:
                return data;
        }
    }

    const generateDataEdit = () => {
        const data = {
            firstName: dataEdit.firstName,
            lastName: dataEdit.lastName,
            oldRole: dataDetail.role,
            newRole: dataEdit.role,
            userType: dataDetail?.userType,
            status: dataEdit.status,
            id
        }

        if (dataEdit.role === Roles.INSPECTING_COMPANY) {
            return { ...data, appId: dataEdit?.inspectingCompany };
        } else if (dataEdit.role === Roles.SHIP_OPERATOR) {
            return { ...data, appId: dataEdit?.shipOperator };
        } else if (dataEdit.role === Roles.REQUESTOR) {
            return { ...data, appId: dataEdit?.requestingCompany };
        }

        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            //navigate('/user-management/users');
            history.back();
            return;
        }

        setIsLoading(true);
        let obj;
        let response: any;
        if (id){
            obj = generateDataEdit();
            response = await putInspectionUsers(obj);
        } else {
            obj = generatedataAdd();
            response = await postInspectionUsers(obj);
        }

        setIsSubmitting(false);
        setIsLoading(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: id ? 'User updated' : 'User registered',
                description: `We've ${id ? 'updated' : 'registered'} this user.`,
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                //navigate('/user-management/users');
                history.back();
            }, 1000)
        } else {
            toast({
                title: `User ${id ? 'update' : 'registration'} failed.`,
                description: response?.message || `We could not ${id ? 'update' : 'register'} this user.`,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeRole = (value: string, props: any) => {
        props.setFieldValue('role', value)
        setSelectedRole(value);
    }

    const getDataListInspector = async () => {
        const response = await getDataInspector() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorId,
                label: item.inspectorName,
            }));
            setListInspector(formattedData);
        } else {
            toast({
                title: 'Failed to get data inspectors.',
                description: "We are unable to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspector([]);
        }
    }

    const getDataListShipOwner = async () => {
        const response = await getShipOwnerData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.shipOwnerId,
                label: item.onBehalfCompany,
            }));
            setListShipOwner(formattedData);
        } else {
            toast({
                title: 'Failed to get data ship owners.',
                description: "We are unable to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListShipOwner([]);
        }
    }

    const getDataListRequestingCompany = async () => {
        const response = await getListSireReviewRequestingCompany();
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: RefData) => ({
                value: item.id,
                label: item.name,
            }));
            setListRequestingCompany(formattedData);
        } else {
            toast({
                title: 'Failed to get data of requesting companies.',
                description: "We are unable to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListRequestingCompany([]);
        }
    }

    const getListRole = async () => {
        const response = await getRoleData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.roleId, label: item.roleName }));
            setListRole(formattedOptions);
        }
    }

    const getDataListType = async () => {
        const response = await getDataRef('usertype') as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                label: item.name,
                value: item.value,
            }));

            setListUserType(formattedData);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    getListRole(),
                    getDataListType(),
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, []);

    useEffect(() => {
        if (selectedRole === Roles.INSPECTING_COMPANY) {
            getDataListInspector();
        } else if (selectedRole === Roles.SHIP_OPERATOR) {
            getDataListShipOwner();
        } else if (selectedRole === Roles.REQUESTOR) {
            getDataListRequestingCompany();
        }
    }, [role, selectedRole]);

    useEffect(() => {
        const obj = {
            role,
            inspectingCompany,
            shipOperator,
            requestingCompany,
            email,
            firstName,
            lastName,
            userType,
            status,
        }
        //console.log('obj', obj)
        setDataDetail(obj);
    }, [id]);

    useEffect(() => {
        setSelectedRole(role);
    }, [role]);

    /*useEffect(() => {
        console.log('dataDetail', dataDetail)
    }, [dataDetail])*/

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                role: dataDetail?.role ?? '' as string,
                                type: dataDetail?.userType ?? '' as string,
                                inspectingCompany: dataDetail?.inspectingCompany ?? '' as string,
                                shipOperator: dataDetail?.shipOperator ?? '' as string,
                                requestingCompany: dataDetail?.requestingCompany ?? '' as string,
                                email: dataDetail?.email ?? '' as string,
                                firstName: dataDetail?.firstName ?? '' as string,
                                lastName: dataDetail?.lastName ?? '' as string,
                                status: dataDetail?.status ?? USER_STATUS.INACTIVE,
                            }}
                            validationSchema={() => addUserSchema(selectedRole)}
                            onSubmit={(value) => {
                                handleSubmit(value);
                            }}
                            enableReinitialize={true}
                        >
                            {(props: FormikProps<FormContents>) => (
                                <Form>
                                    <Field name='role'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.role && form.touched.role} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Role<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectRole name='role' handleChange={({ value }: any) => handleChangeRole(value, props)} value={props.values.role} defaultValue={props.values.role} hasError={form.errors.role && form.touched.role} options={listRole} />
                                                        <FormErrorMessage>{form.errors.role}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='type'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.type && form.touched.type} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectUserTypeRefData name='type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} defaultValue={props.values.type} hasError={form.errors.type && form.touched.type} options={listUserType} isDisabled={Boolean(id)} />
                                                        <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {selectedRole === Roles.INSPECTING_COMPANY && <Field name='inspectingCompany'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.inspectingCompany && form.touched.inspectingCompany} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspecting Company<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='inspectingCompany' handleChange={({ value }: any) => props.setFieldValue('inspectingCompany', value)} value={props.values.inspectingCompany} defaultValue={props.values.inspectingCompany} options={listInspector} hasError={form.errors.inspectingCompany && form.touched.inspectingCompany} />
                                                        <FormErrorMessage>{form.errors.inspectingCompany}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    {selectedRole === Roles.SHIP_OPERATOR && <Field name='shipOperator'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.shipOperator && form.touched.shipOperator} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Ship Operator<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectShipOwner name='shipOperator' handleChange={({ value }: any) => props.setFieldValue('shipOperator', value)} value={props.values.shipOperator} defaultValue={dataDetail?.shipOperator} options={listShipOwner} hasError={form.errors.shipOperator && form.touched.shipOperator} />
                                                        <FormErrorMessage>{form.errors.shipOperator}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    {selectedRole === Roles.REQUESTOR && <Field name='requestingCompany'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.requestingCompany && form.touched.requestingCompany)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Requesting Company<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Select
                                                            name='requestingCompany'
                                                            onChange={(selected) => props.setFieldValue('requestingCompany', selected?.value)}
                                                            value={option(props.values.requestingCompany, listRequestingCompany.find((item) => (item.value === props.values.requestingCompany))?.label ?? '')}
                                                            options={listRequestingCompany}
                                                            styles={getCustomStyles(Boolean(form.errors.requestingCompany && form.touched.requestingCompany))}
                                                        />
                                                        <FormErrorMessage>{String(form.errors.requestingCompany)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    <Field name='email'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.email && form.touched.email} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Email' className="form-input" disabled={Boolean(id)} />
                                                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='firstName'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.firstName && form.touched.firstName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">First Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='First Name' className="form-input" />
                                                        <FormErrorMessage>{form.errors.firstName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='lastName'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.lastName && form.touched.lastName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Last Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Last Name' className="form-input" />
                                                        <FormErrorMessage>{form.errors.lastName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {Boolean(id) && <Field name='status'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor='status-switch' mb='0' className="form-label">
                                                        Status
                                                    </FormLabel>
                                                    <div className="form-input-container">
                                                        <Switch {...field}
                                                            isChecked={props.values.status === USER_STATUS.ACTIVE}
                                                            onChange={(e) => props.setFieldValue('status', e.target.checked ? USER_STATUS.ACTIVE : USER_STATUS.INACTIVE)}
                                                            id='status-switch'
                                                            style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }}
                                                        />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            {id ? "Edit" : "Register"} User
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default EditUser;
